import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import { delay, SectionHeading, tone } from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { useMusicT } from './kit';

/** Three steps from an idea to a finished song. */
export default function HowItWorks() {
  const { t } = useMusicT();
  const reveal = useReveal<HTMLOListElement>(0.2);

  const steps = [
    {
      title: t('Describe your song'),
      body: t(
        'Say what it’s about, who it’s for and how it should feel, or paste lyrics you wrote. A template gives you a ready-made sound.',
      ),
    },
    {
      title: t('Get two versions'),
      body: t(
        'The AI writes the music, sings the words and makes cover art. Every generation gives you two takes to choose from in a few minutes.',
      ),
    },
    {
      title: t('Download and share'),
      body: t(
        'Download MP3 or WAV, share a link or make a lyric video. Change one thing and generate again until it’s right.',
      ),
    },
  ];

  return (
    <section className='py-20 sm:py-24'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          size='md'
          title={t('From an idea to a song.')}
          tail={t('In three steps.')}
        />
        <ol
          ref={reveal.ref}
          data-reveal={reveal.state}
          className={clsxm(
            styles.reveal,
            'mt-14 grid gap-x-10 gap-y-12 border-t border-[#0f0f28]/[0.1] pt-10 md:grid-cols-3 dark:border-white/[0.1]',
          )}
        >
          {steps.map((step, index) => (
            <li
              key={step.title}
              className={styles.rv}
              style={delay(index * 90)}
            >
              <span
                className={clsxm(
                  styles.display,
                  'text-sm font-semibold tabular-nums text-primary-600 dark:text-primary-400',
                )}
              >
                {String(index + 1).padStart(2, '0')}
              </span>
              <h3 className={clsxm('mt-3 text-xl font-semibold', tone.ink)}>
                {step.title}
              </h3>
              <p className={clsxm('mt-3 text-[16px] leading-7', tone.body)}>
                {step.body}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
